import { FormStore } from 'form/reflux/form_store';
import FormFieldFactory from 'form/models/field/form_field_factory';
import { FormAPI, FormActionAPI, BaseAPI, FlashMessageAPI } from 'lecom-api';
import { formatFieldValue } from '../../../form/helpers/field_value_formatter';
import FormFieldTypes from '../../../form/constants/form_field_types';
import { getClassProperties } from 'utils/object_utils';
import { generateUUID } from 'utils/uuid';
import DateUtils from 'utils/date_utils';
import { getApiValueDecimalNumber, changeDecimalString } from 'utils/formatting_utils';

/**
 * @class LabelFormFieldAPI
 * @extends FormFieldAPI
 */
class FormFieldAPI extends BaseAPI {
	_formId;
	_gridId;
	_isGridField;
	_previousValue;

	static fieldProperties(fieldComponent) {
		const fieldModelClass = FormFieldFactory.getModelClass(fieldComponent);
		return getClassProperties(fieldModelClass)
			.filter((p) => ['value', 'required', 'id', 'formId', 'fields', 'actions', 'errors', 'readOnly'].indexOf(p) === -1);
	}

	/**
	 * @constructor
	 * @param {*} param0
	 */
	constructor({ id, storeId, formId, gridId, applyMode = FormAPI.applyMode, isGridField = false }) {
		super({ id, storeId, applyMode, storeClass: FormStore });
		this._formId = formId;
		this._gridId = gridId;
		this._isGridField = isGridField;
		this._previousValue = "";

		FormFieldAPI.fieldProperties(this.get('component')).forEach((fieldProperty) => {
			this[fieldProperty] = this._propertyAccessor.bind(this, fieldProperty);
		});
	}

	get formId() {
		return this._formId;
	}

	get gridId() {
		return this._gridId;
	}

	get isGridField() {
		return this._isGridField;
	}

	readOnly(value) {
		if(value !== undefined && value !== null) {
			if (this.gridId) {
				const formAPI = FormAPI.form(this._formId, this._applyMode);
				const fieldsGrid = formAPI.fields(this.gridId).fields();

				fieldsGrid.forEach(field => {
					field.set({ updateUUID: 'updateUUID' });
				});
			}

			return this.set({ readOnly: value });
		}

		return this.get('readOnly');
	}

	required(value) {
		var validations = this.get("validations");
		var requiredValidation;

		for (let cont = 0, len = validations.length; cont < len; cont++) {
			if (validations[cont].hasOwnProperty("required")) {
				requiredValidation = validations[cont];
				break;
			}
		}

		if(typeof value !== 'undefined') {
			if(!requiredValidation){
				requiredValidation = {}
				validations.push(requiredValidation);
			}

			if (typeof value === 'object') {
				requiredValidation.required = value.value;
				requiredValidation.formActionName = value.formActionName;
			} else {
				let actions = FormAPI.form(this._formId).actions();
				let formActionName;
				for (let cont = 0, len = actions.length; cont < len; cont++) {
					if (actions[cont].id === 'aprovar') {
						formActionName = 'aprovar';
					} else if (actions[cont].id === 'finish') {
						formActionName = 'finish';
					}
				}
				requiredValidation.required = value;
				requiredValidation.formActionName = formActionName;
			}

			return this.set({ validations });
		}

		return requiredValidation && requiredValidation.required ? true : false;
	}

	setRequired(formActionName, required){
		if(this.gridId != undefined && typeof formActionName == "boolean"){
			required = formActionName;
			formActionName = "aprovar";
		}

		return this.validations([{formActionName: formActionName, required: required}]);
	}

	setErrorToMaxLength(value, maxLength, digits, component, prefix) {
		let maxNumber = "";

		for (let index = 0; index < maxLength - (digits || 0); index++) {
			maxNumber += '9';
		}

		if (digits) {
			maxNumber += '.';

			for (let index = 0; index < digits; index++) {
				maxNumber += '9';
			}
		}

		const maxNumberFormatted = changeDecimalString(Number(maxNumber), maxLength, digits, component, prefix, true);

		this.set({value: maxNumberFormatted}).warnings(`${Realize.i18n.t("form.warnings.max_length_numeric_api").replace('@', value)} ${maxNumberFormatted}.`).apply();
	}

	/**
	 *
	 * @param {*} value
	 */
	value(value) {
		const oldValue = value;
		let maxLength = this.get("maxLength");
		let digits = this.get('digits');
		let component = this.get('component');
		let prefix = this.get('prefix');

		if(value !== undefined && value !== null) {
			if (this.type() === FormFieldTypes.TEXT || this.type() === FormFieldTypes.TEXTAREA && this.type() !== FormFieldTypes.NUMBER){
				value = formatFieldValue(value.toString().substr(0, maxLength));
			}

			if (this.type() === FormFieldTypes.NUMBER){
				if (maxLength !== undefined && maxLength !== null) {
					value = typeof value === "number" ? value.toFixed(digits || 0) : value;

					const valueString = String(value).trim();

					if (valueString && valueString.replace(/[^\d]/g, '').length > maxLength) {
						this.setErrorToMaxLength(oldValue, maxLength, digits, component, prefix);
						return this;
					}
				}

				return this.set({ value: changeDecimalString(value, maxLength, digits, component, prefix, true), rawValue: value, warnings: '' })
			}

			if (this.type() === FormFieldTypes.CURRENCY) {
				if (maxLength !== undefined && maxLength !== null) {
					value = typeof value === "number" ? value.toFixed(2) : value;

					const valueString = String(value).trim();
					const maxLengthCurrency = 15;

					if (valueString && valueString.replace(/[^\d]/g, '').length > maxLengthCurrency) {
						this.setErrorToMaxLength(oldValue, maxLengthCurrency, 2, component, prefix);
						return this;
					}
				}

				return this.set({ value: changeDecimalString(value, maxLength, digits, component, prefix, true), rawValue: value, warnings: '' })
			}

			if (this.type() === FormFieldTypes.DECIMAL) {
				if (maxLength !== undefined && maxLength !== null && digits !== undefined && digits !== null) {
					value = typeof value === "number" ? value.toFixed(digits || 0) : value;
					
					const valueString = String(value).trim();

					if (valueString && valueString.replace(/[^\d]/g, "").length > maxLength) {
						this.setErrorToMaxLength(oldValue, maxLength, digits, component, prefix);
						return this;
					}
				}

				return this.set({ value: changeDecimalString(value, maxLength, digits, component, prefix, true), rawValue: value, warnings: '' })
			}

			return this.set({ value: value, rawValue: value, warnings: '' });
		}

		if (['DECIMAL', 'CURRENCY', 'NUMBER'].includes(this.type()))
			return getApiValueDecimalNumber(this.get("value"), prefix);

		if (this.type() === 'AUTOCOMPLETE') {
			const valueAutocomplete = this.get("value");

			if(valueAutocomplete && Array.isArray(valueAutocomplete) && valueAutocomplete.length) {
				if (valueAutocomplete[0] === undefined || valueAutocomplete[0] === null || valueAutocomplete[0] === "")
					return undefined;

				return [formatFieldValue(valueAutocomplete[0])];
			}
		}

		if(this.type() === 'DATE' && this.get("value") !== undefined && this.get("value") !== null){
			const date = this.get("value");

			if (DateUtils.isISO8601DateGlobal(date)) {
				return DateUtils.convertDateStringToMomentGlobal(date);
			}

			return date;
		}

		return this.get("value");
	}

	/**
	 *
	 * @param {*} properties
	 */
	get(...properties) {
		const propertyValues = this.isGridField
			? this.storeClass.getGridFieldProperties(this.storeId, this.gridId, this.id, ...properties)
			: this.storeClass.getFieldProperties(this.storeId, this.id, ...properties);

		if (!properties)  {
			return propertyValues;
		}

		return (properties.length === 1) ? propertyValues[properties[0]] : this.getProperties(propertyValues);
	}

	/**
	 *
	 * @param {*} properties
	 */
	set(properties) {
		const isInEagerMode = this._isInEagerMode();
		const storeActions = this.storeClass.listenables;
		const storeEvents = this.storeClass.events;
		properties['updateUUID'] = generateUUID();

		this.isGridField
			? storeActions.setGridFieldProperties(this.storeId, this.gridId, this.id, properties, isInEagerMode)
			: storeActions.setFieldProperties(this.storeId, this.id, properties, isInEagerMode);

		if(properties.rawValue != undefined && properties.rawValue !== null) //&& this._previousValue != properties.value
			setTimeout(() => this.publish(storeEvents.SET_FIELD_VALUE, properties.value), 0);

		this._previousValue = properties.value;

		return isInEagerMode ? this._buildEventPromise(storeEvents.SET_FIELD_PROPERTIES) : this;
	}

	/**
	 *
	 * @param {*} actionId
	 */
	actions(actionId) {
		const actionsAPIs = this._buildActionsAPIs();
		return actionId ? actionsAPIs.find((f) => f.id === actionId) : actionsAPIs;
	}

	/**
	 *
	 */
	loadInitialValue() {
		const storeActions = this.storeClass.listenables;
		const storeEvents = this.storeClass.events;

		storeActions.loadFieldInitialValue(this.storeId, this.id);
		return this._buildEventPromise(storeEvents.FIELD_INITIAL_VALUE_LOAD, storeEvents.FIELD_INITIAL_VALUE_LOAD_ERROR);
	}

	/**
	 *
	 */
	focus() {
		$("#" + this.id).trigger("focus");
		return this;
	}

	blur() {
		$("#" + this.id).trigger("blur");
		return this;
	}

	removeMask() {
		return this.set({ mask: "" });
	}

	/**
	 *
	 * @param {*} errors
	 */
	errors(errors) {
		if(errors === undefined)
			return this._getErrors();

		return this._setErrors(errors);
	}

	addMessageModal(objInfo) {
		const documentModalId = 'flash-document-modal';

		FlashMessageAPI.flash(documentModalId).addMessage({
			message: objInfo.mensagem || '',
			type: objInfo.type || 'error'
		});

		return this;
	}

	document() {
		const fieldId = this.get('id');
		const storeDocument = Lecom.stores.DocumentStore.store[fieldId];

		if (!storeDocument)
			return null;

		const { id, fileUUID, fileName, version:{currentVersion} } = storeDocument.document;

		return {
			id,
			fileUUID,
			fileName,
			currentVersion
		};
	}

	serialize() {
		let value = this.get("value");

		switch (this.type()) {
			case FormFieldTypes.DATE:
				return DateUtils.convertDateStringToISOString(value);
				break;
			case FormFieldTypes.AUTOCOMPLETE:
				if (Array.isArray(value) && !value.length)
					value = null;

				return value;
				break;
			case FormFieldTypes.RADIO:
				if (Array.isArray(value) && !value.length)
					value = null;

				return value;
				break;
			default:
				return value;
				break;
		}
	}

	/**
	 *
	 * @param {*} event
	 * @param {*} args
	 */
	publish(event, ...args) {
		const storeEventHandler = this.storeClass.eventHandler;
		return storeEventHandler.publish(event, this.storeId, this.id, ...args);
	}

	/**
	 *
	 */
	apply() {
		return super.apply(this.id);
	}

	/**
	 * @private
	 * @param {*} eventHandler
	 * @param {*} eventName
	 * @param {*} elementStoreId
	 * @param {*} elementFieldIdOrObject
	 * @param {*} eventArgs
	 */
	_eventHandlerWrapper(eventHandler, eventName, elementStoreId, elementFieldIdOrObject, ...eventArgs) {
		if(typeof eventHandler === 'function'
			&& elementStoreId === this.storeId
			&& ((typeof elementFieldIdOrObject === 'object' && elementFieldIdOrObject.fieldId === this.id) ||
			(elementFieldIdOrObject === this.id))
		) {
			eventHandler(elementStoreId, elementFieldIdOrObject, ...eventArgs);
		}
	}

	/**
	 * @private
	 */
	_buildActionsAPIs() {
		const actions = this.get('actions');
		return actions.map((action) => new FormActionAPI({
			id: action.id,
			gridId: this.gridId,
			fieldId: this.id,
			formId: this.formId,
			storeId: this.storeId,
			applyMode: this.applyMode,
			isGridField: this.isGridField,
			isFieldAction: true,
		}));
	}

	_getErrors() {
		const formAPI = FormAPI.form(this._formId, this._applyMode);
		const formErrors = formAPI.errors();

		if(formErrors && typeof formErrors === 'object')
			return formErrors[this._id];

		return [];
	}

	_setErrors(errors) {
		const isInEagerMode = this._isInEagerMode();
		const storeEvents = this.storeClass.events;
 		const formAPI = FormAPI.form(this._formId, this._applyMode);
 		const formErrors = formAPI.errors() || {};

		if (Array.isArray(errors)) {
			if (errors.length)
				formErrors[this._id] = errors;
			else if (formErrors.hasOwnProperty(this._id))
				delete formErrors[this._id];
		}
		else if (errors)
			formErrors[this._id] = [errors];
		else if (formErrors.hasOwnProperty(this._id))
			delete formErrors[this._id];

		this.set({ updateUUID: 'updateUUID' });
		formAPI.errors(formErrors);

		return isInEagerMode ? this._buildEventPromise(storeEvents.SET_PROPERTIES) : this;
	}
};

export default FormFieldAPI
